/**
 * Open charges, one card per lease — the page behind the dashboard's "Open
 * charges" stat. Deliberately not in the nav: it's a drill-down from that
 * number, not a destination of its own.
 *
 * Reads `GET /api/ledger/open-charges-by-lease`, which groups server-side so
 * the per-lease totals are `lease_balances`' own figures. Money is rendered
 * verbatim, never summed here — same rule as `OpenChargesTable`.
 */

import { useList } from '@refinedev/core';
import { Link } from 'react-router';
import { humanizeEnum } from '../../../components/Form.js';

interface OpenChargesLeaseGroup {
  leaseId: string;
  propertyId: string;
  propertyName: string;
  unitLabel: string;
  totalBalance: string;
  pastDueBalance: string;
  maxDaysLate: number;
  pendingPaymentAmount: string;
  tenants: { id: string; name: string; isPrimary: boolean }[];
  owners: { id: string; displayName: string }[];
  charges: {
    chargeId: string;
    chargeType: string;
    dueDate: string | null;
    amount: string;
    balance: string;
    daysLate: number;
  }[];
}

export function OpenChargesByLease() {
  const { data, isLoading, error } = useList<OpenChargesLeaseGroup>({
    resource: 'ledger/open-charges-by-lease',
    pagination: { mode: 'off' },
  });
  const groups = data?.data ?? [];

  return (
    <section>
      <h1>Open charges</h1>
      <p className="muted">
        Every lease carrying an open charge, most overdue first. For the same balances as a table,
        see <Link to="/staff/delinquency">Delinquency</Link> or the{' '}
        <Link to="/staff/rent-roll">rent roll</Link>.
      </p>

      {isLoading ? <p className="muted">Loading…</p> : null}
      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load open charges.'}
        </p>
      ) : null}
      {!isLoading && !error && groups.length === 0 ? <p className="muted">Nothing open.</p> : null}

      {groups.length > 0 ? (
        <div className="charge-card-grid">
          {groups.map((group) => (
            <LeaseChargesCard key={group.leaseId} group={group} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function LeaseChargesCard({ group }: { group: OpenChargesLeaseGroup }) {
  const pending = Number(group.pendingPaymentAmount);
  const pastDue = Number(group.pastDueBalance);

  return (
    <article className="charge-card">
      <header className="charge-card__header">
        <div>
          <Link to={`/staff/leases/${group.leaseId}`} className="charge-card__title">
            {group.propertyName} · {group.unitLabel}
          </Link>
          <div className="charge-card__pills">
            {group.maxDaysLate > 0 ? (
              <span className="pill pill--late">{group.maxDaysLate}d late</span>
            ) : (
              <span className="pill">current</span>
            )}
            {pending > 0 ? (
              <span className="pill pill--info">${group.pendingPaymentAmount} processing</span>
            ) : null}
          </div>
        </div>
        <div className="charge-card__total num">
          <strong>${group.totalBalance}</strong>
          {pastDue > 0 ? <span className="muted">${group.pastDueBalance} past due</span> : null}
        </div>
      </header>

      <dl className="charge-card__people">
        <div>
          <dt className="muted">{group.tenants.length === 1 ? 'Tenant' : 'Tenants'}</dt>
          <dd>
            {group.tenants.length === 0 ? (
              <span className="muted">None on lease</span>
            ) : (
              group.tenants.map((tenant, i) => (
                <span key={tenant.id}>
                  {i > 0 ? ', ' : null}
                  <Link to={`/staff/tenants/${tenant.id}/edit`}>{tenant.name}</Link>
                  {tenant.isPrimary && group.tenants.length > 1 ? (
                    <span className="muted"> (primary)</span>
                  ) : null}
                </span>
              ))
            )}
          </dd>
        </div>
        <div>
          <dt className="muted">{group.owners.length === 1 ? 'Owner' : 'Owners'}</dt>
          <dd>
            {group.owners.length === 0 ? (
              <span className="muted">No active owner</span>
            ) : (
              group.owners.map((owner, i) => (
                <span key={owner.id}>
                  {i > 0 ? ', ' : null}
                  <Link to={`/staff/owners/${owner.id}/edit`}>{owner.displayName}</Link>
                </span>
              ))
            )}
          </dd>
        </div>
      </dl>

      <table className="table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Due</th>
            <th className="num">Amount</th>
            <th className="num">Balance</th>
            <th>Age</th>
          </tr>
        </thead>
        <tbody>
          {group.charges.map((charge) => (
            <tr key={charge.chargeId}>
              <td>{humanizeEnum(charge.chargeType)}</td>
              <td>{charge.dueDate ?? '—'}</td>
              <td className="num">${charge.amount}</td>
              <td className="num">${charge.balance}</td>
              <td>
                {charge.daysLate > 0 ? (
                  <span className="pill pill--late">{charge.daysLate}d</span>
                ) : (
                  <span className="pill">current</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
}
